import React, { useState, useCallback } from 'react';
import get from 'lodash.get';
import { NavLink, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  ContentLoader,
  NotFound,
  Notification,
  Page,
  Tab,
  Tabs,
  TabContent,
  TabsHeader,
  Button,
} from 'shared/components';
import { useAPI } from 'shared/hooks';
import {
  isUserManager,
  hasPermission,
  parseObject,
  getPortalData,
  displayValue,
} from 'shared/helpers';
import { ResetLicenseForm } from 'licenses/components';
import EditLicenseForm from 'licenses/components/EditLicenseForm';
import { initialPortalData } from 'shared/constants';
import LicenseDetails from './LicenseDetails';
import LicenseDevices from './LicenseDevices';
import LicenseUsers from './LicenseUsers';
import ProductFeatures from './ProductFeatures';
import BundleLicenseContainer from '../BundleLicenseContainer';
import CustomFields from './CustomFields';
import LicenseOAuth from './LicenseOAuth';

const LicenseContainer = () => {
  const { t } = useTranslation();
  const match = useRouteMatch();
  const userDetails = useSelector((state) => get(state, 'user.details'));
  const managedCustomerAccounts = useSelector((state) =>
    get(state, 'account.customerAccounts', []),
  );
  const isManager = isUserManager(userDetails);
  const licenseID = get(match, 'params.licenseId');
  const companyID = String(get(userDetails, 'company.id'));
  const portal_data = get(userDetails, 'company.portal_data');
  const portalData = parseObject(portal_data, initialPortalData);
  const { hideCustomFields, hideProductFeatures } = portalData;
  const canEditLicense = hasPermission('edit_license');
  const canResetLicense = hasPermission('reset_license');
  const canViewOAuth = hasPermission('show_license_oauth_clients');
  const canManageOAuth = hasPermission('manage_license_oauth_clients');
  const [showEditLicenseForm, setEditLicenseFormDisplay] = useState(false);
  const [isResetFormDisplayed, setResetFormDisplay] = useState(false);
  const allowUsersToResetLicense = getPortalData(
    portalData,
    'allowUsersLicenseReset',
    initialPortalData,
  );

  const [shouldGetLicenseAgain, setShouldGetLicenseAgain] = useState(0);

  const handleLicenseReset = () => {
    if (!canResetLicense && !allowUsersToResetLicense) {
      Notification(
        'error',
        t('Missing permission'),
        t('You do not have permission to perform this action.'),
      );
    } else {
      setResetFormDisplay(!isResetFormDisplayed);
    }
  };

  const onErrorCallback = useCallback(
    () =>
      Notification(
        'error',
        t('Error occured'),
        t('There was an error while getting license data'),
      ),
    [t],
  );

  const { isLoading: loading, value: license = null } = useAPI({
    url: `/api/v1/licenses/${licenseID}/`,
    onError: onErrorCallback,
    dependenciesArray: [licenseID, shouldGetLicenseAgain],
  });

  const refetchLicense = () =>
    setShouldGetLicenseAgain(shouldGetLicenseAgain + 1);

  if (loading) {
    return (
      <Page
        title=""
        breadcrumb={<NavLink to="/licenses">{t('Licenses')}</NavLink>}
      >
        <ContentLoader text={t('Getting license details')} />
      </Page>
    );
  }

  if (!license) {
    return (
      <Page
        title=""
        breadcrumb={<NavLink to="/licenses">{t('Licenses')}</NavLink>}
      >
        <NotFound />
      </Page>
    );
  }

  const licenseProductName =
    get(license, 'product.product_name') ||
    get(license, 'product.name') ||
    get(license, 'product_name', '');
  const licenseAuthorizationMethod =
    get(license, 'product.authorization_method') ||
    get(license, 'authorization_method');
  const product = get(license, 'product', {});

  const displayLicenseTitle = () => {
    const licenseKey = get(license, 'license_key', '');
    const licenseUsers = get(license, 'license_users', []);
    const isFloating =
      get(license, 'is_floating') || get(license, 'is_floating_cloud');
    if (licenseKey && !licenseUsers.length) {
      return (
        <>
          {`${t('License')}: ${licenseKey}`}
          {isFloating && <span className="title-tag">{t('Floating')}</span>}
        </>
      );
    }
    return (
      <>
        {`${t('License for')}: ${displayValue(licenseProductName)}`}
        {isFloating && <span className="title-tag">{t('Floating')}</span>}
      </>
    );
  };

  // redundacy for edge cases when manager can also view licenses where he is only user
  const isLicenseManager = get(license, 'is_manager');

  const isUserAuth = licenseAuthorizationMethod === 'user';
  const isBundleLicense = get(license, 'is_bundle');

  const isLicenseUser = !isManager || !isLicenseManager;
  const showUserTab = isUserAuth && !isLicenseUser;

  if (isBundleLicense) {
    return (
      <Page
        title={displayLicenseTitle()}
        breadcrumb={<NavLink to="/licenses">{t('Licenses')}</NavLink>}
      >
        <div className="LicenseContainer BundleLicenseContainer">
          <BundleLicenseContainer
            license={license}
            bundleProduct={product}
            refetchLicense={refetchLicense}
            isLicenseUser={isLicenseUser}
          />
        </div>
      </Page>
    );
  }

  const isAirGapped = get(license, 'is_air_gapped');

  const licenseCustomerAccount = get(license, 'customer_account');
  const licenseCustomerAccountID = get(licenseCustomerAccount, 'id');
  const licenseCustomerAccountLabel =
    get(licenseCustomerAccount, 'name') || licenseCustomerAccountID;
  const canNavigateToLicenseCustomerAccount = managedCustomerAccounts.some(
    (customerAccount) =>
      String(get(customerAccount, 'id')) === String(licenseCustomerAccountID),
  );

  return (
    <Page
      title={displayLicenseTitle()}
      breadcrumb={<NavLink to="/licenses">{t('Licenses')}</NavLink>}
      description={
        licenseCustomerAccount ? (
          <div className="LicenseContainer-account-info">
            <div className="LicenseContainer-account-wrapper">
              {canNavigateToLicenseCustomerAccount ? (
                <NavLink to={`/customer-accounts/${licenseCustomerAccountID}`}>
                  {licenseCustomerAccountLabel}
                </NavLink>
              ) : (
                <span>{licenseCustomerAccountLabel}</span>
              )}
            </div>
          </div>
        ) : null
      }
    >
      <div className="list-header">
        <div>
          {canEditLicense && (
            <Button
              theme="info"
              onClick={() => setEditLicenseFormDisplay(!showEditLicenseForm)}
              disabled={isAirGapped}
            >
              {t('Edit license')}
            </Button>
          )}
        </div>
        <div>
          {canResetLicense && (
            <Button theme="warning" onClick={handleLicenseReset}>
              {t('Reset license')}
            </Button>
          )}
        </div>
      </div>
      <div className="LicenseContainer">
        <Tabs>
          <TabsHeader>
            <Tab>{t('License details')}</Tab>
            {!hideProductFeatures && <Tab>{t('Product features')}</Tab>}
            {!hideCustomFields && <Tab>{t('Custom fields')}</Tab>}
            <Tab>{t('Devices')}</Tab>
            {showUserTab && <Tab>{t('License users')}</Tab>}
            {isLicenseManager && (canManageOAuth || canViewOAuth) && (
              <Tab>{t('OAuth')}</Tab>
            )}
          </TabsHeader>
          <TabContent>
            <LicenseDetails
              license={license}
              getLicense={refetchLicense}
              isLicenseUser={isLicenseUser}
            />
          </TabContent>
          {!hideProductFeatures && (
            <TabContent>
              <ProductFeatures
                license={license}
                refetchLicense={refetchLicense}
              />
            </TabContent>
          )}
          {!hideCustomFields && (
            <TabContent>
              <CustomFields license={license} />
            </TabContent>
          )}
          <TabContent>
            <LicenseDevices license={license} isLicenseUser={isLicenseUser} />
          </TabContent>
          {showUserTab && (
            <TabContent>
              <LicenseUsers license={license} getLicense={refetchLicense} />
            </TabContent>
          )}
          {isLicenseManager && (canManageOAuth || canViewOAuth) && (
            <TabContent>
              <LicenseOAuth licenseID={licenseID} companyID={companyID} />
            </TabContent>
          )}
        </Tabs>
      </div>
      {isResetFormDisplayed && (
        <ResetLicenseForm
          close={() => setResetFormDisplay(false)}
          confirmCb={() => setResetFormDisplay(false)}
          license={license}
          getLicense={refetchLicense}
        />
      )}
      {showEditLicenseForm && (
        <EditLicenseForm
          closeCb={() => setEditLicenseFormDisplay(false)}
          isOpen={showEditLicenseForm}
          license={license}
          refetchLicense={refetchLicense}
          currentProduct={product}
        />
      )}
    </Page>
  );
};

export default LicenseContainer;
